import * as React from "react";

/**
 * Lazy-loaded Lottie wrapper. Lottie's runtime is heavy (~100KB gz), so we
 * defer it via React.lazy + Suspense. Animation data is fetched with the
 * component; pass a URL or a JSON object.
 *
 * Respects prefers-reduced-motion by rendering a static fallback frame.
 */

const LazyLottie = React.lazy(async () => {
  const mod = await import("lottie-react");
  return { default: mod.default };
});

export interface LottiePlayerProps {
  src: string | object;
  loop?: boolean;
  autoplay?: boolean;
  className?: string;
  fallback?: React.ReactNode;
}

export function LottiePlayer({
  src,
  loop = true,
  autoplay = true,
  className,
  fallback = null,
}: LottiePlayerProps) {
  const [data, setData] = React.useState<object | null>(typeof src === "object" ? src : null);
  const [error, setError] = React.useState<Error | null>(null);

  React.useEffect(() => {
    if (typeof src !== "string") return;
    let cancelled = false;
    fetch(src)
      .then((r) => r.json())
      .then((json) => {
        if (!cancelled) setData(json);
      })
      .catch((err) => {
        if (!cancelled) setError(err);
      });
    return () => {
      cancelled = true;
    };
  }, [src]);

  const reduce = useReducedMotion();

  if (error || !data) return <>{fallback}</>;
  if (reduce) return <>{fallback}</>;

  return (
    <React.Suspense fallback={fallback}>
      <LazyLottie animationData={data} loop={loop} autoplay={autoplay} className={className} />
    </React.Suspense>
  );
}

function useReducedMotion(): boolean {
  const [reduce, setReduce] = React.useState(false);
  React.useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduce(mq.matches);
    const onChange = () => setReduce(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return reduce;
}
